import { useCallback, useEffect, useMemo, useState, type DragEvent } from 'react'
import { api, ApiError, type CategoryAttribute, type CategoryNode } from '../api/client'
import { useAuth } from '../auth/AuthProvider'
import { Button, Checkbox, ConfirmDialog, EmptyState, Field, Input, Notice, Select, Spinner } from '../ui/controls'

/**
 * Категории и их набор атрибутов (пп. 4.3, 4.5).
 *
 * Дерево слева, карточка выбранной категории справа. Перетаскивание узла
 * меняет родителя и порядок; пути ветви пересчитывает серверная часть
 * (А-203, А-204).
 */
export default function CategoriesPage() {
  const { state } = useAuth()
  const canWrite = state.status === 'authenticated' && state.me.permissions.write.catalog

  const [tree, setTree] = useState<CategoryNode[] | null>(null)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [expanded, setExpanded] = useState<Set<number>>(new Set())
  const [search, setSearch] = useState('')
  const [notice, setNotice] = useState<{ kind: 'error' | 'success' | 'warning'; text: string } | null>(null)

  const reload = useCallback(async () => {
    try {
      const list = await api.categoryTree()
      setTree(list)
      // Верхний уровень раскрыт сразу: полностью свёрнутое дерево скрывает
      // структуру каталога и заставляет раскрывать узлы вручную при каждом
      // входе в раздел.
      setExpanded((prev) => {
        if (prev.size > 0) return prev
        return new Set(list.filter((n) => n.children?.length).map((n) => n.id))
      })
      return list
    } catch (e) {
      setNotice({ kind: 'error', text: e instanceof ApiError ? e.message : 'Не удалось загрузить дерево' })
      setTree([])
      return []
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  const flat = useMemo(() => flatten(tree ?? []), [tree])
  const selected = flat.find((n) => n.node.id === selectedId)?.node ?? null

  // Поиск раскрывает найденные узлы и подсвечивает их (А-209).
  const matches = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return null
    return new Set(
      flat
        .filter(({ node }) => node.name_ru.toLowerCase().includes(q) || node.code.includes(q))
        .map(({ node }) => node.id),
    )
  }, [search, flat])

  useEffect(() => {
    if (!matches) return
    const open = new Set(expanded)
    for (const { node, parents } of flat) {
      if (matches.has(node.id)) parents.forEach((p) => open.add(p))
    }
    setExpanded(open)
    // Раскрытие выполняется один раз на изменение поиска.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  const visible = flat.filter(({ parents }) => parents.every((p) => expanded.has(p)))

  if (tree === null) return <Spinner label="Загрузка дерева категорий…" />

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
      <section>
        <header className="mb-3 flex items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-ink-900 dark:text-ink-50">Категории</h2>
          {canWrite && (
            <Button
              size="sm"
              variant="primary"
              onClick={() => setSelectedId(-1)}
              title="Создать корневую категорию"
            >
              + Категория
            </Button>
          )}
        </header>

        <Input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Поиск по названию или коду"
          aria-label="Поиск по дереву категорий"
          className="mb-3"
        />

        {notice && (
          <Notice kind={notice.kind} onClose={() => setNotice(null)}>
            {notice.text}
          </Notice>
        )}

        {tree.length === 0 ? (
          <EmptyState
            title="Дерево категорий пустое"
            hint="Начните с корневой категории — например, «Автотовары». Вложенность не ограничена, на практике достаточно четырёх уровней."
            action={
              canWrite ? (
                <Button variant="primary" onClick={() => setSelectedId(-1)}>
                  Создать первую категорию
                </Button>
              ) : undefined
            }
          />
        ) : (
          <ul className="rounded-lg border border-ink-200 py-1 dark:border-ink-800">
            {visible.map(({ node, depth }) => (
              <TreeRow
                key={node.id}
                node={node}
                depth={depth}
                selected={node.id === selectedId}
                highlighted={matches?.has(node.id) ?? false}
                expanded={expanded.has(node.id)}
                canWrite={canWrite}
                onToggle={() => {
                  const next = new Set(expanded)
                  next.has(node.id) ? next.delete(node.id) : next.add(node.id)
                  setExpanded(next)
                }}
                onSelect={() => setSelectedId(node.id)}
                onDropInto={async (draggedId, mode) => {
                  try {
                    if (mode === 'inside') {
                      await api.moveCategory(draggedId, node.id, 0)
                    } else {
                      await api.moveCategory(draggedId, node.parent_id, node.position + 1)
                    }
                    setNotice({ kind: 'success', text: 'Перемещено' })
                    await reload()
                  } catch (e) {
                    setNotice({
                      kind: 'error',
                      text: e instanceof ApiError ? e.message : 'Не удалось переместить',
                    })
                  }
                }}
              />
            ))}
          </ul>
        )}
      </section>

      <section>
        {selectedId === -1 ? (
          <CategoryForm
            key="new"
            parentId={null}
            canWrite={canWrite}
            onSaved={async (id) => {
              await reload()
              setSelectedId(id)
              setNotice({ kind: 'success', text: 'Категория создана' })
            }}
            onCancel={() => setSelectedId(null)}
          />
        ) : selected ? (
          <CategoryDetails
            key={selected.id}
            category={selected}
            canWrite={canWrite}
            allCategories={flat.map((f) => f.node)}
            onChanged={async () => {
              await reload()
            }}
            onDeleted={async () => {
              setSelectedId(null)
              await reload()
              setNotice({ kind: 'success', text: 'Удалено' })
            }}
            onCreateChild={() => setSelectedId(-2)}
          />
        ) : selectedId === -2 && selected === null ? null : (
          <EmptyState
            title="Категория не выбрана"
            hint="Выберите категорию слева, чтобы изменить её или настроить набор атрибутов."
          />
        )}
      </section>
    </div>
  )
}

// --------------------------------------------------------------------------
// Дерево
// --------------------------------------------------------------------------

type FlatRow = { node: CategoryNode; depth: number; parents: number[] }

function flatten(nodes: CategoryNode[], depth = 0, parents: number[] = []): FlatRow[] {
  const out: FlatRow[] = []
  for (const n of nodes) {
    out.push({ node: n, depth, parents })
    if (n.children?.length) {
      out.push(...flatten(n.children, depth + 1, [...parents, n.id]))
    }
  }
  return out
}

function TreeRow({
  node,
  depth,
  selected,
  highlighted,
  expanded,
  canWrite,
  onToggle,
  onSelect,
  onDropInto,
}: {
  node: CategoryNode
  depth: number
  selected: boolean
  highlighted: boolean
  expanded: boolean
  canWrite: boolean
  onToggle: () => void
  onSelect: () => void
  onDropInto: (draggedId: number, mode: 'inside' | 'after') => void
}) {
  const [dropMode, setDropMode] = useState<'inside' | 'after' | null>(null)
  const hasChildren = (node.children?.length ?? 0) > 0

  function onDragOver(e: DragEvent) {
    if (!canWrite) return
    e.preventDefault()
    // Верхняя часть строки — вложить внутрь, нижняя — поставить следом.
    const rect = e.currentTarget.getBoundingClientRect()
    setDropMode(e.clientY - rect.top < rect.height * 0.6 ? 'inside' : 'after')
  }

  return (
    <li
      draggable={canWrite}
      onDragStart={(e) => e.dataTransfer.setData('text/plain', String(node.id))}
      onDragOver={onDragOver}
      onDragLeave={() => setDropMode(null)}
      onDrop={(e) => {
        e.preventDefault()
        const dragged = Number(e.dataTransfer.getData('text/plain'))
        const mode = dropMode
        setDropMode(null)
        if (dragged && dragged !== node.id && mode) onDropInto(dragged, mode)
      }}
      className={`relative ${dropMode === 'inside' ? 'bg-brand-50 dark:bg-brand-500/15' : ''} ${
        dropMode === 'after' ? 'border-b-2 border-brand-500' : ''
      }`}
    >
      <div
        className={`flex items-center gap-1 py-1 pr-2 text-sm ${
          selected ? 'bg-brand-50 dark:bg-brand-500/15 text-brand-700 dark:text-brand-300' : 'hover:bg-ink-50 dark:hover:bg-ink-900'
        }`}
        style={{ paddingLeft: `${depth * 1.15 + 0.5}rem` }}
      >
        <button
          type="button"
          onClick={onToggle}
          aria-label={expanded ? 'Свернуть' : 'Развернуть'}
          className={`flex size-5 shrink-0 items-center justify-center text-xs text-ink-400 ${
            hasChildren ? 'hover:text-brand-600' : 'invisible'
          }`}
        >
          {expanded ? '▾' : '▸'}
        </button>

        <button
          type="button"
          onClick={onSelect}
          className="flex min-w-0 flex-1 items-baseline gap-2 text-left"
        >
          <span
            className={`truncate ${
              node.is_active ? 'text-ink-800 dark:text-ink-200' : 'text-ink-400 line-through'
            } ${highlighted ? 'bg-warning-100 dark:bg-warning-700/25 text-warning-700 dark:text-warning-100' : ''}`}
          >
            {node.name_ru}
          </span>
          <span className="shrink-0 text-xs tabular-nums text-ink-400">
            {node.products_direct}
            {node.products_total !== node.products_direct && ` / ${node.products_total}`}
          </span>
        </button>
      </div>
    </li>
  )
}

// --------------------------------------------------------------------------
// Карточка категории
// --------------------------------------------------------------------------

function CategoryDetails({
  category,
  canWrite,
  allCategories,
  onChanged,
  onDeleted,
}: {
  category: CategoryNode
  canWrite: boolean
  allCategories: CategoryNode[]
  onChanged: () => Promise<void>
  onDeleted: () => Promise<void>
  onCreateChild: () => void
}) {
  const [tab, setTab] = useState<'main' | 'attributes'>('main')

  return (
    <div>
      <header className="mb-4">
        <h2 className="text-lg font-semibold text-ink-900 dark:text-ink-50">{category.name_ru}</h2>
        <p className="mt-0.5 text-sm text-ink-500">
          Товаров: {category.products_direct} в самой категории, {category.products_total} с учётом
          вложенных
        </p>
      </header>

      <div className="mb-5 flex gap-1 border-b border-ink-200 dark:border-ink-800">
        {(
          [
            ['main', 'Основные сведения'],
            ['attributes', 'Атрибуты'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
              tab === key
                ? 'border-brand-600 text-brand-700 dark:text-brand-300'
                : 'border-transparent text-ink-500 hover:text-ink-700 dark:hover:text-ink-200'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'main' ? (
        <CategoryForm
          category={category}
          parentId={category.parent_id}
          canWrite={canWrite}
          onSaved={onChanged}
          onDeleted={onDeleted}
        />
      ) : (
        <CategoryAttributes
          categoryId={category.id}
          canWrite={canWrite}
          allCategories={allCategories}
        />
      )}
    </div>
  )
}

function CategoryForm({
  category,
  parentId,
  canWrite,
  onSaved,
  onCancel,
  onDeleted,
}: {
  category?: CategoryNode
  parentId: number | null
  canWrite: boolean
  onSaved: (id: number) => void | Promise<void>
  onCancel?: () => void
  onDeleted?: () => Promise<void>
}) {
  const [nameRu, setNameRu] = useState(category?.name_ru ?? '')
  const [nameKk, setNameKk] = useState(category?.name_kk ?? '')
  const [code, setCode] = useState(category?.code ?? '')
  const [description, setDescription] = useState(category?.description_ru ?? '')
  const [isActive, setIsActive] = useState(category?.is_active ?? true)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [dirty, setDirty] = useState(false)

  // Изменение кода у категории с товарами меняет адреса на витрине (А-208).
  const codeChanged = category !== undefined && code !== category.code
  const hasProducts = (category?.products_total ?? 0) > 0

  async function save() {
    setPending(true)
    setError(null)
    try {
      const body = {
        name_ru: nameRu.trim(),
        name_kk: nameKk.trim() || null,
        code: code.trim() || undefined,
        description_ru: description.trim() || null,
        is_active: isActive,
        parent_id: parentId,
      }
      const saved = category
        ? await api.updateCategory(category.id, body)
        : await api.createCategory(body)
      setDirty(false)
      await onSaved(saved.id)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Не удалось сохранить')
    } finally {
      setPending(false)
    }
  }

  return (
    <div className="max-w-xl space-y-4">
      {error && <Notice kind="error">{error}</Notice>}
      {codeChanged && hasProducts && (
        <Notice kind="warning">
          Код изменён. Адреса категории и её товаров на витрине изменятся, прежние ссылки
          перестанут работать.
        </Notice>
      )}

      <Field label="Название (рус)">
        <Input
          value={nameRu}
          onChange={(e) => {
            setNameRu(e.target.value)
            setDirty(true)
          }}
          disabled={!canWrite || pending}
          required
        />
      </Field>

      <Field label="Название (каз)" hint="Необязательно">
        <Input
          value={nameKk}
          onChange={(e) => {
            setNameKk(e.target.value)
            setDirty(true)
          }}
          disabled={!canWrite || pending}
        />
      </Field>

      <Field
        label="Код"
        hint="Участвует в адресе категории на витрине. Если оставить пустым, будет предложен по названию."
      >
        <Input
          value={code}
          onChange={(e) => {
            setCode(e.target.value)
            setDirty(true)
          }}
          disabled={!canWrite || pending}
          placeholder="например, moto-helmets"
          className="font-mono"
        />
      </Field>

      <Field label="Описание для страницы категории" hint="Необязательно">
        <textarea
          value={description}
          onChange={(e) => {
            setDescription(e.target.value)
            setDirty(true)
          }}
          disabled={!canWrite || pending}
          rows={3}
          className="w-full rounded-md border border-ink-300 bg-white px-3 py-2 text-sm text-ink-900 shadow-xs focus:border-brand-500 disabled:opacity-60 dark:border-ink-700 dark:bg-ink-900 dark:text-ink-50"
        />
      </Field>

      <Checkbox
        label="Активна"
        hint="Неактивная категория и её товары не попадают на витрину"
        checked={isActive}
        onChange={(e) => {
          setIsActive(e.target.checked)
          setDirty(true)
        }}
        disabled={!canWrite || pending}
      />

      {canWrite && (
        <div className="flex items-center gap-2 pt-1">
          <Button variant="primary" onClick={save} disabled={pending || nameRu.trim() === ''}>
            {pending ? 'Сохранение…' : category ? 'Сохранить' : 'Создать'}
          </Button>
          {onCancel && (
            <Button onClick={onCancel} disabled={pending}>
              Отмена
            </Button>
          )}
          {category && onDeleted && (
            <Button variant="danger" onClick={() => setConfirmDelete(true)} disabled={pending}>
              Удалить
            </Button>
          )}
          {dirty && <span className="text-xs text-warning-700 dark:text-warning-100">Есть несохранённые изменения</span>}
        </div>
      )}

      {confirmDelete && category && onDeleted && (
        <ConfirmDialog
          title="Удалить категорию?"
          message={
            <>
              Категория «{category.name_ru}» будет удалена без возможности восстановления.
              Удаление доступно только для категории без товаров и подкатегорий.
            </>
          }
          confirmLabel="Удалить"
          pending={pending}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={async () => {
            setPending(true)
            setError(null)
            try {
              await api.deleteCategory(category.id)
              setConfirmDelete(false)
              await onDeleted()
            } catch (e) {
              setConfirmDelete(false)
              if (e instanceof ApiError) {
                const d = e.details as { products?: number; subcategories?: number }
                setError(
                  `${e.message}. Товаров: ${d.products ?? 0}, подкатегорий: ${d.subcategories ?? 0}.`,
                )
              } else {
                setError('Не удалось удалить')
              }
            } finally {
              setPending(false)
            }
          }}
        />
      )}
    </div>
  )
}

// --------------------------------------------------------------------------
// Набор атрибутов категории
// --------------------------------------------------------------------------

function CategoryAttributes({
  categoryId,
  canWrite,
  allCategories,
}: {
  categoryId: number
  canWrite: boolean
  allCategories: CategoryNode[]
}) {
  const [list, setList] = useState<CategoryAttribute[] | null>(null)
  const [available, setAvailable] = useState<{ id: number; name_ru: string; code: string }[]>([])
  const [notice, setNotice] = useState<{ kind: 'error' | 'success' | 'warning'; text: string } | null>(null)
  const [adding, setAdding] = useState<Set<number>>(new Set())
  const [copyFrom, setCopyFrom] = useState<number | ''>('')

  const reload = useCallback(async () => {
    try {
      const [bound, all] = await Promise.all([api.categoryAttributes(categoryId), api.attributes()])
      setList(bound)
      const boundIds = new Set(bound.map((b) => b.attribute_id))
      setAvailable(all.filter((a) => !boundIds.has(a.id)).map((a) => ({ id: a.id, name_ru: a.name_ru, code: a.code })))
    } catch (e) {
      setNotice({ kind: 'error', text: e instanceof ApiError ? e.message : 'Не удалось загрузить' })
      setList([])
    }
  }, [categoryId])

  useEffect(() => {
    void reload()
  }, [reload])

  if (list === null) return <Spinner label="Загрузка набора атрибутов…" />

  const own = list.filter((a) => !a.inherited)
  const inherited = list.filter((a) => a.inherited)

  return (
    <div className="max-w-3xl">
      {notice && (
        <Notice kind={notice.kind} onClose={() => setNotice(null)}>
          {notice.text}
        </Notice>
      )}

      <h3 className="mb-2 text-sm font-semibold text-ink-900 dark:text-ink-50">
        Собственные атрибуты
      </h3>
      {own.length === 0 ? (
        <p className="mb-5 text-sm text-ink-500">
          В самой категории атрибуты не заданы. Добавьте их ниже — они будут действовать
          и во всех вложенных категориях.
        </p>
      ) : (
        <ul className="mb-5 divide-y divide-ink-200 rounded-lg border border-ink-200 dark:divide-ink-800 dark:border-ink-800">
          {own.map((a) => (
            <li key={a.attribute_id} className="flex items-center gap-3 px-3 py-2.5">
              <div className="min-w-0 flex-1">
                <div className="text-sm text-ink-800 dark:text-ink-200">{a.name_ru}</div>
                <div className="font-mono text-xs text-ink-500">{a.code}</div>
              </div>
              <label className="flex items-center gap-2 text-xs text-ink-600 dark:text-ink-400">
                <input
                  type="checkbox"
                  checked={a.is_required}
                  disabled={!canWrite}
                  onChange={async (e) => {
                    try {
                      const res = await api.setAttributeRequired(
                        categoryId,
                        a.attribute_id,
                        e.target.checked,
                      )
                      if (res.missing_required && res.missing_required > 0) {
                        setNotice({
                          kind: 'warning',
                          text: `Сохранено. Товаров без значения этого атрибута: ${res.missing_required}. Опубликовать их не получится, пока значение не заполнено.`,
                        })
                      } else {
                        setNotice({ kind: 'success', text: 'Сохранено' })
                      }
                      await reload()
                    } catch (err) {
                      setNotice({
                        kind: 'error',
                        text: err instanceof ApiError ? err.message : 'Не удалось сохранить',
                      })
                    }
                  }}
                  className="size-3.5 rounded border-ink-300"
                />
                обязательный
              </label>
              {canWrite && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={async () => {
                    try {
                      await api.unbindAttribute(categoryId, a.attribute_id)
                      setNotice({ kind: 'success', text: 'Атрибут отвязан' })
                      await reload()
                    } catch (err) {
                      setNotice({
                        kind: 'error',
                        text: err instanceof ApiError ? err.message : 'Не удалось отвязать',
                      })
                    }
                  }}
                >
                  Отвязать
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {inherited.length > 0 && (
        <>
          <h3 className="mb-2 text-sm font-semibold text-ink-900 dark:text-ink-50">
            Унаследованные атрибуты
          </h3>
          <p className="mb-2 text-xs text-ink-500">
            Заданы в родительских категориях и действуют здесь автоматически. Отвязать их можно
            только в категории-владельце.
          </p>
          <ul className="mb-5 divide-y divide-ink-200 rounded-lg border border-ink-200 bg-ink-50 dark:divide-ink-800 dark:border-ink-800 dark:bg-ink-900">
            {inherited.map((a) => (
              <li key={a.attribute_id} className="flex items-center gap-3 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <span className="text-sm text-ink-700 dark:text-ink-300">{a.name_ru}</span>
                  {a.is_required && (
                    <span className="ml-2 text-xs text-warning-700 dark:text-warning-100">обязательный</span>
                  )}
                </div>
                <span className="shrink-0 text-xs text-ink-500">из «{a.owner_category_name}»</span>
              </li>
            ))}
          </ul>
        </>
      )}

      {canWrite && (
        <div className="rounded-lg border border-ink-200 p-4 dark:border-ink-800">
          <h3 className="mb-3 text-sm font-semibold text-ink-900 dark:text-ink-50">
            Добавить атрибуты
          </h3>

          {available.length === 0 ? (
            <p className="text-sm text-ink-500">
              Все атрибуты справочника уже привязаны. Новые заводятся в разделе «Атрибуты».
            </p>
          ) : (
            <>
              <div className="mb-3 flex max-h-48 flex-wrap gap-1.5 overflow-y-auto">
                {available.map((a) => {
                  const on = adding.has(a.id)
                  return (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => {
                        const next = new Set(adding)
                        on ? next.delete(a.id) : next.add(a.id)
                        setAdding(next)
                      }}
                      className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${
                        on
                          ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/15 text-brand-700 dark:text-brand-300'
                          : 'border-ink-300 text-ink-600 hover:border-brand-400 dark:border-ink-700'
                      }`}
                    >
                      {a.name_ru}
                    </button>
                  )
                })}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  disabled={adding.size === 0}
                  onClick={async () => {
                    await api.bindAttributes(categoryId, [...adding], false)
                    setAdding(new Set())
                    setNotice({ kind: 'success', text: 'Атрибуты привязаны' })
                    await reload()
                  }}
                >
                  Привязать выбранные ({adding.size})
                </Button>
                <Button
                  size="sm"
                  disabled={adding.size === 0}
                  onClick={async () => {
                    await api.bindAttributes(categoryId, [...adding], true)
                    setAdding(new Set())
                    setNotice({ kind: 'success', text: 'Привязаны как обязательные' })
                    await reload()
                  }}
                >
                  Привязать как обязательные
                </Button>
              </div>
            </>
          )}

          <div className="mt-4 flex flex-wrap items-end gap-2 border-t border-ink-200 pt-4 dark:border-ink-800">
            <div className="min-w-56 flex-1">
              <Field label="Скопировать набор из категории">
                <Select value={copyFrom} onChange={(e) => setCopyFrom(e.target.value ? Number(e.target.value) : '')}
                  className="w-full rounded-md border border-ink-300 bg-white px-3 py-2 text-sm text-ink-900 dark:border-ink-700 dark:bg-ink-900 dark:text-ink-50"
                >
                  <option value="">— выберите категорию —</option>
                  {allCategories
                    .filter((c) => c.id !== categoryId)
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {' '.repeat((c.depth - 1) * 2)}
                        {c.name_ru}
                      </option>
                    ))}
                </Select>
              </Field>
            </div>
            <Button
              size="sm"
              disabled={copyFrom === ''}
              onClick={async () => {
                const res = await api.copyAttributes(categoryId, Number(copyFrom))
                setCopyFrom('')
                setNotice({ kind: 'success', text: `Скопировано атрибутов: ${res.added}` })
                await reload()
              }}
            >
              Скопировать
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
